import { DataGrid, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Pagination,
  Tooltip,
} from "@mui/material";
import DatasetModal from "./DatasetModal";
import MouseClickList from "../MouseClickList";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { MoreHorizIcon } from "../../icons";
import {
  deleteDataset,
  getDatasetInfo,
  getDataSets,
  getDSImgCount,
  getUnassignedDataSets,
} from "common-lib";

export default function DatasetTable({
  page,
  setPage,
  keyword,
  total,
  setTotal,
}: {
  page: number;
  setPage: (page: number) => void;
  keyword: string | null;
  total: number;
  setTotal: (count: number) => void;
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [optionOpen, setOptionOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clickedDataset, setClickedDataset] = useState<any>(null); //row 클릭된 DS
  const [selectedDatasetId, setSelectedDatasetId] = useState<number | null>(
    null
  ); //

  const [unassignedList, setUnassignedList] = useState<DatasetItem[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "name", headerName: "Name", width: 200 },
    { field: "description", headerName: "Description", width: 400 },
    {
      field: "Labeled / Total",
      headerName: "Labeled / Total",
      width: 200,
      renderCell: (params: any) => (
        <span>
          {params.row.info.labeled_files} / {params.row.info.total_files}
        </span>
      ),
    },
    // {
    //   field: "",
    //   headerName: "",
    //   width: 100,
    //   renderCell: (params: any) => (
    //     <IconButton onClick={(event) => handleMoreButtonClick(event, params)}>
    //       <MoreHorizIcon />
    //     </IconButton>
    //   ),
    // },
  ];
  const modifiedColumns = columns.map((column) => ({
    ...column,
  }));

  const handleMoreButtonClick = (
    event: React.MouseEvent<HTMLElement>,
    params: GridRowParams
  ) => {
    event.stopPropagation();
    console.log("Clicked row:", params.row);
  };

  // 프로젝트에 할당되지 않은 데이터셋 리스트 조회
  const fetchUnassignedDatasets = async () => {
    try {
      const response = await getUnassignedDataSets();
      setUnassignedList(response.dataSets);
    } catch (error) {
      console.error(error);
    }
  };

  // 전체 데이터셋 조회
  const fetchDataSets = async () => {
    setIsLoading(true);
    try {
      const response = await getDataSets({ page: page, size: 20 });
      console.log(response);
      setTotal(response.data.total_count);
      const count = await getDSImgCount({ page: page, size: 20 });

      const datasets = response.data.contents;
      const counts = count.data.contents;

      // id 기준 병합
      const merged = datasets.map((ds: { id: number }) => {
        const matched = counts.find((c: { id: number }) => c.id === ds.id);
        return {
          ...ds,
          info: {
            total_files: matched?.totalFiles ?? 0,
            labeled_files: matched?.labeledFiles ?? 0,
          },
        };
      });

      setRows(merged);
    } catch (error) {
      console.error("Error fetching trains:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDataSets();
  }, [page]);

  useEffect(() => {
    fetchUnassignedDatasets();
  }, []);

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const selectedId = event.currentTarget.getAttribute("data-id");
    setSelectedDatasetId(Number(selectedId));
    setAnchorEl(event.currentTarget);
    setOptionOpen(true);
  };

  const handleRowClick = async (params: GridRowParams) => {
    const rowData = params.row;
    try {
      const datasetInfo = await getDatasetInfo(rowData.id);
      // const responseData = response.data;
      setIsModalOpen(true);

      const isUnassigned = unassignedList.some(
        (ds) => ds.id === datasetInfo.data.id
      );
      setClickedDataset({ ...datasetInfo.data, isBlock: !isUnassigned });
    } catch (error) {
      console.error(error);
    }
  };

  const handleClose = () => {
    setOptionOpen(false);
    setAnchorEl(null);
  };

  const handleEdit = () => {
    navigate(`/datasets/edit/${selectedDatasetId}`);
  };

  const handleDelete = async () => {
    try {
      if (window.confirm("정말 삭제하시겠습니까?")) {
        setOptionOpen(false);
        if (selectedDatasetId) {
          await deleteDataset(selectedDatasetId);
        }
        fetchDataSets();
      }
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  return (
    <>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Grid container sx={{ mt: 3 }}>
            <DataGrid
              hideFooter
              hideFooterPagination
              disableColumnMenu
              disableColumnSorting
              rows={rows}
              columns={modifiedColumns}
              onRowClick={handleRowClick}
              getRowId={(row) => row.id}
              slotProps={{
                row: {
                  onContextMenu: handleContextMenu,
                  style: { cursor: "context-menu" },
                },
              }}
              // onRowClick={handleRowClick}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "var(--dg-table-header-bg-color)",
                  color: "#fff",
                },
                "& .MuiDataGrid-filler": {
                  backgroundColor: "var(--dg-table-header-bg-color)",
                  color: "#fff",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-virtualScrollerContent": {
                  backgroundColor: "var(--dk-surface, white)",
                },
                "& .MuiDataGrid-cell": {
                  cursor: "pointer",
                  backgroundColor: "var(--unselected-row-bg)",
                  color: "var(--dg-color)",
                },
              }}
            />
            <MouseClickList
              anchorEl={anchorEl}
              open={optionOpen}
              category="dataset"
              onEdit={handleEdit}
              onClose={handleClose}
              onDelete={handleDelete}
            />
            {isModalOpen && (
              <DatasetModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                dataset={clickedDataset}
              />
            )}
          </Grid>
          <Pagination
            count={Math.ceil(total / 20)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="medium"
            sx={{ mt: 2, display: "flex", justifyContent: "center" }}
          />
        </Box>
      )}
    </>
  );
}
